import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Checkbox } from '../ui/checkbox';
import { Button } from '../ui/button';
import { Upload } from 'lucide-react';

interface QuestionAnswerPreviewProps {
  /** One of the QualificationQuestion question types. */
  questionType: string;
  /** Defined answer options (used by single_choice / multiple_choice). */
  choices: string[];
  /** Stable key used to scope radio/checkbox element ids. */
  idKey: string;
}

/**
 * Read-only render of the control a supplier will use to answer a question of
 * the given type. Single source of truth for "how this question type looks" —
 * shared by the supplier-facing Preview dialog (TemplatePreview) and the inline
 * answer preview in the template builder (QuestionEditor), so the two can never
 * drift. All controls are disabled; this is for display only.
 */
export function QuestionAnswerPreview({ questionType, choices, idKey }: QuestionAnswerPreviewProps) {
  switch (questionType) {
    case 'text':
      return <Input disabled placeholder="Enter your answer..." />;
    case 'textarea':
      return <Textarea disabled placeholder="Enter your answer..." rows={3} />;
    case 'number':
      return <Input type="number" disabled placeholder="Enter a number..." />;
    case 'date':
      return <Input type="date" disabled />;
    case 'yes_no':
      return (
        <RadioGroup disabled className="flex gap-4">
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="yes" id={`preview-${idKey}-yes`} />
            <Label htmlFor={`preview-${idKey}-yes`} className="text-black">Yes</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="no" id={`preview-${idKey}-no`} />
            <Label htmlFor={`preview-${idKey}-no`} className="text-black">No</Label>
          </div>
        </RadioGroup>
      );
    case 'single_choice':
      return (
        <RadioGroup disabled className="space-y-2">
          {choices.length > 0 ? (
            choices.map((choice, idx) => (
              <div key={idx} className="flex items-center space-x-2">
                <RadioGroupItem value={choice || `opt-${idx}`} id={`preview-${idKey}-${idx}`} />
                <Label htmlFor={`preview-${idKey}-${idx}`} className="text-black">{choice || `Option ${idx + 1}`}</Label>
              </div>
            ))
          ) : (
            <p className="text-sm text-black italic">No choices defined yet</p>
          )}
        </RadioGroup>
      );
    case 'multiple_choice':
      return (
        <div className="space-y-2">
          {choices.length > 0 ? (
            choices.map((choice, idx) => (
              <div key={idx} className="flex items-center space-x-2">
                <Checkbox id={`preview-${idKey}-${idx}`} disabled />
                <Label htmlFor={`preview-${idKey}-${idx}`} className="text-black">{choice || `Option ${idx + 1}`}</Label>
              </div>
            ))
          ) : (
            <p className="text-sm text-black italic">No choices defined yet</p>
          )}
        </div>
      );
    case 'rating':
      return (
        <div className="flex gap-2">
          {[1, 2, 3, 4, 5].map((rating) => (
            <Button key={rating} variant="outline" size="sm" disabled>
              {rating}
            </Button>
          ))}
        </div>
      );
    case 'file_upload':
      return (
        <div className="border-2 border-dashed rounded-lg p-6 text-center">
          <Upload className="h-8 w-8 mx-auto text-black" />
          <span className="text-sm text-black block mt-2">Click to upload a file</span>
          <span className="text-xs text-black">PDF, DOC, DOCX, XLS, XLSX, PNG, JPG up to 10MB</span>
        </div>
      );
    default:
      return <Input disabled placeholder="Enter your answer..." />;
  }
}
